/**
 * Subscriptions API Client
 *
 * API Endpoints:
 * - GET /plans/list → Plan[]
 * - GET /plans/by-country → Plan[]
 * - GET /subscription/status → { is_subscribed: boolean }
 * - GET /subscription → BackendSubscriptionInfo
 * - POST /redeem-codes/validate → { valid: boolean, plan: Plan }
 * - POST /redeem-codes/apply → { message: string, subscribed: boolean }
 * - POST /subscription/create → Subscription (Apple)
 * - POST /subscription/google-play/create → Subscription (Google Play)
 */

import { captureError } from '@/shared/lib/errors/capture';
import { pickPrimaryRow, tierOf } from '@/features/subscription/lib/tier';
import type {
  BackendSubscriptionRow,
  BackendSubscriptionInfo,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  GrantFreeDaysRequest,
  GrantFreeDaysResponse,
  PauseSubscriptionRequest,
  PauseSubscriptionResponse,
  ResumeSubscriptionResponse,
  SubscriptionRowSummary,
} from '@/types/subscription';
import * as Sentry from '@sentry/nextjs';
import { isAxiosError } from 'axios';
import type { ApiResponse, Plan, Subscription, SubscriptionInfo } from '../types';
import { apiClient } from './base-client';

export interface GetPlansParams {
  email?: string | undefined;
  ref?: string | undefined;
  country_code?: string | undefined;
}

export interface RedeemCodeValidateResponse {
  valid: boolean;
  message: string;
  code_info?: {
    type: 'invite' | 'gift';
    value: number;
    redirect_url: string | null;
  };
}

export interface RedeemCodeApplyResponse {
  message: string;
  subscription: {
    id: number;
    start_at: string;
    end_at: string;
    status: string;
  };
}

export interface InviteInfo {
  id: number;
  uuid: string;
  code: string;
  type: 'invite' | 'gift';
  value: number;
  description: string | null;
  redirect_url: string | null;
  max_uses: number | null;
  uses_count: number;
  is_active: boolean;
  start_date: string | null;
  expiry_date: string | null;
  created_at: string;
}

export interface MyInviteResponse {
  has_invite: boolean;
  invite: InviteInfo | null;
  can_create_invite: boolean;
}

export interface CreateInviteResponse {
  message: string;
  invite: InviteInfo;
}

export const subscriptionsClient = {
  /**
   * Get all available plans with Whop checkout URLs
   */
  getPlans: async (params?: GetPlansParams): Promise<Plan[]> => {
    const queryParams: Record<string, unknown> = {};
    if (params?.email) queryParams.email = params.email;
    if (params?.ref) queryParams.ref = params.ref;
    if (params?.country_code) queryParams.country_code = params.country_code;

    const data = await apiClient.get<{ plans?: Plan[]; data?: Plan[] } | Plan[]>('/plans/list', {
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    });
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') {
      if ('plans' in data && Array.isArray(data.plans)) return data.plans;
      if ('data' in data && Array.isArray(data.data)) return data.data;
    }
    return [];
  },

  /**
   * Get plans filtered by country (for regional pricing)
   */
  getPlansByCountry: async (country?: string): Promise<Plan[]> => {
    const data = await apiClient.get<{ plans?: Plan[]; data?: Plan[] } | Plan[]>(
      '/plans/by-country',
      {
        params: { country } as Record<string, unknown>,
      }
    );
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') {
      if ('plans' in data && Array.isArray(data.plans)) return data.plans;
      if ('data' in data && Array.isArray(data.data)) return data.data;
    }
    return [];
  },

  /**
   * Quick subscription status check
   */
  getStatus: async (): Promise<{ is_subscribed: boolean }> => {
    const data = await apiClient.get<{ is_subscribed?: boolean; subscribed?: boolean }>(
      '/subscription/status'
    );
    return { is_subscribed: data.is_subscribed || data.subscribed || false };
  },

  /**
   * Get full subscription details from backend
   */
  getSubscription: async (): Promise<BackendSubscriptionInfo | null> => {
    try {
      const data = await apiClient.get<
        BackendSubscriptionInfo | ApiResponse<Subscription> | Subscription
      >('/subscription');
      // BackendSubscriptionInfo: `subscriptions[]` (+ deprecated top-level
      // fields). An empty list still counts — `lifetime_access` can be true
      // with no rows.
      if (data && typeof data === 'object' && ('subscriptions' in data || 'start_at' in data)) {
        return data as BackendSubscriptionInfo;
      }
      // Handle ApiResponse<Subscription> wrapper
      if (data && typeof data === 'object' && 'data' in data) {
        const sub = (data as ApiResponse<Subscription>).data;
        return {
          provider: sub.provider || null,
          status: sub.status || null,
          start_at: sub.starts_at || null,
          end_at: sub.expires_at || null,
          manage_url: sub.payload?.manage_url || null,
        };
      }
      // Handle raw Subscription shape
      const sub = data as Subscription;
      if (sub && sub.provider) {
        return {
          provider: sub.provider || null,
          status: sub.status || null,
          start_at: sub.starts_at || null,
          end_at: sub.expires_at || null,
          manage_url: sub.payload?.manage_url || null,
        };
      }
      return null;
    } catch (error) {
      captureError(error, { component: 'SubscriptionsClient', action: 'apply-redeem-code' });
      return null;
    }
  },

  /**
   * Get subscription info with manage_url for billing page
   * Derives from getSubscription()
   */
  getSubscriptionInfo: async (): Promise<SubscriptionInfo> => {
    try {
      const raw = await subscriptionsClient.getSubscription();
      if (raw) return toSubscriptionInfo(raw);
      return { is_subscribed: false };
    } catch (error) {
      captureError(error, { component: 'SubscriptionsClient', action: 'get-status' });
      return { is_subscribed: false };
    }
  },

  /**
   * Validate a redeem code
   */
  validateRedeemCode: async (code: string): Promise<RedeemCodeValidateResponse> => {
    return Sentry.startSpan(
      { op: 'http.client', name: 'POST /redeem-codes/validate' },
      async (span) => {
        span?.setAttribute('flow', 'redeem');
        try {
          const res = await apiClient.post<RedeemCodeValidateResponse>('/redeem-codes/validate', {
            code,
          });
          span?.setAttribute('redeem.valid', !!res.valid);
          return res;
        } catch (err) {
          if (isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
            const data = err.response.data as Record<string, unknown>;
            if (typeof data.message === 'string') {
              span?.setAttribute('redeem.valid', false);
              return { valid: false, message: data.message };
            }
          }
          throw err;
        }
      }
    );
  },

  /**
   * Apply a redeem code to activate subscription
   */
  applyRedeemCode: async (code: string): Promise<RedeemCodeApplyResponse> => {
    return Sentry.startSpan(
      { op: 'http.client', name: 'POST /redeem-codes/apply' },
      async (span) => {
        span?.setAttribute('flow', 'redeem');
        return apiClient.post<RedeemCodeApplyResponse>('/redeem-codes/apply', { code });
      }
    );
  },

  /**
   * Get the current user's invite code (if any)
   */
  getMyInvite: async (): Promise<MyInviteResponse> => {
    return apiClient.get<MyInviteResponse>('/redeem-codes/my-invite');
  },

  /**
   * Generate a new invite code
   */
  createInvite: async (): Promise<CreateInviteResponse> => {
    return apiClient.post<CreateInviteResponse>('/redeem-codes/create-invite', {});
  },

  /**
   * Cancel subscription (Whop in-app cancellation)
   */
  cancelSubscription: async (
    data: CancelSubscriptionRequest
  ): Promise<CancelSubscriptionResponse> => {
    return apiClient.post<CancelSubscriptionResponse>('/subscription/cancel', data);
  },

  /**
   * Pause subscription (Whop pause + BE-owned auto-resume cron)
   */
  pauseSubscription: async (data: PauseSubscriptionRequest): Promise<PauseSubscriptionResponse> => {
    return apiClient.post<PauseSubscriptionResponse>('/subscription/pause', data);
  },

  /**
   * Resume a paused subscription. Idempotent — 200 no-op if already active.
   */
  resumeSubscription: async (): Promise<ResumeSubscriptionResponse> => {
    return apiClient.post<ResumeSubscriptionResponse>('/subscription/resume', {});
  },

  /**
   * Grant free days as a retention save. Server enforces a 3-month cooldown
   * (returns 409 COOLDOWN_ACTIVE with `next_eligible_at`).
   */
  grantFreeDays: async (data: GrantFreeDaysRequest = {}): Promise<GrantFreeDaysResponse> => {
    return apiClient.post<GrantFreeDaysResponse>('/subscription/grant-free-days', data);
  },

  /**
   * Create subscription via Apple In-App Purchase
   */
  createApple: async (receipt: string): Promise<Subscription> => {
    const data = await apiClient.post<ApiResponse<Subscription>>('/subscription/create', {
      receipt,
    });
    return data.data;
  },

  /**
   * Create subscription via Google Play
   */
  createGooglePlay: async (purchaseToken: string, productId: string): Promise<Subscription> => {
    const data = await apiClient.post<ApiResponse<Subscription>>(
      '/subscription/google-play/create',
      {
        purchase_token: purchaseToken,
        product_id: productId,
      }
    );
    return data.data;
  },
};

function toRowSummary(row: BackendSubscriptionRow): SubscriptionRowSummary {
  return {
    uuid: row.uuid,
    provider: row.provider,
    status: row.status,
    tier: tierOf(row.plan),
    end_at: row.end_at,
    cancel_at_period_end: row.cancel_at_period_end ?? false,
  };
}

/**
 * Reads the account from `subscriptions[]`, never from the deprecated
 * top-level fields: the primary row is chosen with the BE's tie-break, so a
 * Tier 1 row ending later never stands in for a Pro row. Pre-rows responses
 * (top-level only) still map. `free_days_grant` only exists top-level, where
 * the BE computes it for the same primary row.
 */
export function toSubscriptionInfo(raw: BackendSubscriptionInfo): SubscriptionInfo {
  const rows = raw.subscriptions ?? [];
  const primary = pickPrimaryRow(rows);
  const sub = primary ?? (raw.start_at ? raw : undefined);
  const lifetime = raw.lifetime_access ? { lifetime_access: true } : {};
  if (!sub) return { is_subscribed: false, ...lifetime };
  const ordered = primary ? [primary, ...rows.filter((r) => r !== primary)] : [];
  return {
    is_subscribed: sub.status === 'active' || sub.status === 'trial',
    ...lifetime,
    ...(primary
      ? {
          uuid: primary.uuid,
          tier: tierOf(primary.plan),
          ...(primary.plan?.tier_name !== undefined ? { tier_name: primary.plan.tier_name } : {}),
          ...(primary.plan?.type ? { plan_type: primary.plan.type } : {}),
          subscriptions: ordered.map(toRowSummary),
        }
      : {}),
    ...(sub.provider && { provider: sub.provider as SubscriptionInfo['provider'] }),
    ...(sub.status && { status: sub.status as SubscriptionInfo['status'] }),
    ...(sub.end_at && { current_period_end: sub.end_at }),
    ...(sub.manage_url && { manage_url: sub.manage_url }),
    ...(sub.can_cancel_in_app !== undefined ? { can_cancel_in_app: sub.can_cancel_in_app } : {}),
    ...(sub.cancel_at_period_end !== undefined
      ? { cancel_at_period_end: sub.cancel_at_period_end }
      : {}),
    ...(sub.whop_status != null ? { whop_status: sub.whop_status } : {}),
    ...(sub.paused_until !== undefined ? { paused_until: sub.paused_until } : {}),
    ...(sub.pause_scheduled_for !== undefined
      ? { pause_scheduled_for: sub.pause_scheduled_for }
      : {}),
    ...(sub.start_at !== undefined ? { start_at: sub.start_at } : {}),
    ...(raw.free_days_grant ? { free_days_grant: raw.free_days_grant } : {}),
  };
}
